import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import { User, Search, ArrowRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DoctorSidebar from '../../components/doctor/DoctorSidebar';
import AppointmentService from '../../services/appoinment.service';
import { getStatusLabel, getStatusColor } from '../../utils/appointmentUtils';
import { formatDate, formatTime } from '../../utils/dateUtils';


const DoctorPatientsPage = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [patientAppointments, setPatientAppointments] = useState({});
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      const appointments = await AppointmentService.getDoctorAppointments();

      // Crea un Map per raggruppare gli appuntamenti per paziente
      const patientMap = new Map();

      appointments.forEach(appointment => {
        const patientId = appointment.patient_id;
        const patientName = appointment.patient_name || `Paziente #${patientId}`;

        if (!patientMap.has(patientId)) {
          patientMap.set(patientId, {
            id: patientId,
            name: patientName,
            appointments: []
          });
        }

        patientMap.get(patientId).appointments.push(appointment);
      });

      // Converti la Map in un array di pazienti con le informazioni aggiuntive
      const patientsList = Array.from(patientMap.values()).map(patient => {
        // Ordina gli appuntamenti per data
        const sortedAppointments = [...patient.appointments].sort(
          (a, b) => new Date(a.date + 'T' + a.time).getTime() - new Date(b.date + 'T' + b.time).getTime()
        );

        // Trova il primo appuntamento (inizio terapia)
        const firstAppointment = sortedAppointments[0];

        // Conta gli appuntamenti completati
        const completedAppointments = sortedAppointments.filter(
          app => app.status === 'confirmed'
        ).length;

        // Trova il prossimo appuntamento (futuro)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcomingAppointments = sortedAppointments.filter(app => {
          const appDate = new Date(app.date);
          appDate.setHours(0, 0, 0, 0);
          return appDate.getTime() >= today.getTime() && app.status !== 'cancelled';
        }).sort(
          (a, b) => new Date(a.date + 'T' + a.time).getTime() - new Date(b.date + 'T' + b.time).getTime()
        );

        const nextAppointment = upcomingAppointments.length > 0 ? upcomingAppointments[0] : null;

        // Trova l'ultimo appuntamento completato
        const completedAppointmentsSorted = sortedAppointments
          .filter(app => app.status === 'confirmed')
          .sort(
            (a, b) => new Date(b.date + 'T' + b.time).getTime() - new Date(a.date + 'T' + a.time).getTime()
          );

        const lastCompletedAppointment = completedAppointmentsSorted.length > 0
          ? completedAppointmentsSorted[0]
          : null;

        // Determina lo stato del paziente (senza "Terapia conclusa")
        let status;
        if (completedAppointments === 0) {
          status = "Nuovo";
        } else {
          status = "In terapia";
        }

        return {
          ...patient,
          startDate: firstAppointment ? formatDate(firstAppointment.date) : 'N/D',
          totalSessions: completedAppointments,
          lastSession: lastCompletedAppointment ? formatDate(lastCompletedAppointment.date) : 'N/D',
          nextSession: nextAppointment ? formatDate(nextAppointment.date) : 'N/D',
          nextSessionTime: nextAppointment ? formatTime(nextAppointment.time) : null,
          nextSessionDetail: nextAppointment
            ? `${formatDate(nextAppointment.date)} - ${formatTime(nextAppointment.time)}`
            : 'Nessuna seduta programmata',
          status: status,
          issue: sortedAppointments[0]?.service_name || 'N/D'
        };
      });

      setPatients(patientsList);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento dei pazienti:', err);
      setError('Non è stato possibile caricare i pazienti. Riprova più tardi.');
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadPatientAppointments = async (patientId) => {
    try {
      setLoadingAppointments(true);

      // Simulo il caricamento degli appuntamenti per un paziente specifico
      const appointments = await AppointmentService.getDoctorAppointments();
      const patientAppointments = appointments.filter(app => app.patient_id === patientId);

      // Ordina gli appuntamenti per data (dal più recente)
      const sortedAppointments = [...patientAppointments].sort(
        (a, b) => new Date(b.date + 'T' + b.time).getTime() - new Date(a.date + 'T' + a.time).getTime()
      );

      setPatientAppointments(prevState => ({
        ...prevState,
        [patientId]: sortedAppointments
      }));

      return sortedAppointments;
    } catch (err) {
      console.error(`Errore nel caricamento degli appuntamenti per il paziente ${patientId}:`, err);
      throw err;
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handlePatientClick = (id) => {
    navigate(`/doctor/patients/${id}`);
  };

  const handleViewAppointments = async (e, patientId) => {
    e.stopPropagation(); // Previene la propagazione dell'evento click

    try {
      // Trova il paziente corrispondente
      const patient = patients.find(p => p.id === patientId);
      setSelectedPatient(patient);

      // Carica gli appuntamenti se non sono già stati caricati
      if (!patientAppointments[patientId]) {
        await loadPatientAppointments(patientId);
      }

      setShowAppointmentsModal(true);
    } catch (err) {
      console.error('Errore nel caricamento degli appuntamenti:', err);
      alert('Non è stato possibile caricare gli appuntamenti del paziente. Riprova più tardi.');
    }
  };

 

  // Filtra i pazienti in base al termine di ricerca
  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.issue.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">I Miei Pazienti</h1>
              <p className="text-gray-600">Gestisci e monitora i tuoi pazienti</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Cerca pazienti..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
            </div>
          </div>
        </header>

        {loadingPatients ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-gray-600">Caricamento pazienti in corso...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        ) : patients.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun paziente trovato
            </h3>
            <p className="text-gray-500">
              Non hai ancora pazienti registrati.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Paziente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Info Terapia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sedute
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Prossima Seduta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPatients.map((patient) => (
                  <tr
                    key={patient.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handlePatientClick(patient.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{patient.name}</div>
                          <div className="text-sm text-gray-500">Inizio: {patient.startDate}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{patient.issue}</div>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(patient.status)}`}>
                        {patient.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{patient.totalSessions} sedute completate</div>
                      <div className="text-sm text-gray-500">
                        {patient.lastSession !== 'N/D'
                          ? `Ultima: ${patient.lastSession}`
                          : 'Nessuna seduta completata'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {patient.nextSession !== 'N/D'
                          ? (
                            <>
                              {patient.nextSession}
                              {patient.nextSessionTime && (
                                <span className="ml-2 text-gray-600">
                                  ore {patient.nextSessionTime}
                                </span>
                              )}
                            </>
                          )
                          : 'Nessuna seduta programmata'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-3 justify-end">
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={(e) => handleViewAppointments(e, patient.id)}
                          title="Visualizza appuntamenti"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          className="text-purple-600 hover:text-purple-900"
                          title="Dettagli paziente"
                        >
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modale per visualizzare gli appuntamenti del paziente */}
      {showAppointmentsModal && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                Appuntamenti - {selectedPatient.name}
              </h3>
              <button
                onClick={() => setShowAppointmentsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {loadingAppointments ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Caricamento appuntamenti in corso...</p>
              </div>
            ) : patientAppointments[selectedPatient.id]?.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                <p>Il paziente non ha ancora appuntamenti registrati.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servizio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {patientAppointments[selectedPatient.id]?.map((appointment) => (
                      <tr key={appointment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatDate(appointment.date)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatTime(appointment.time)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{appointment.service_name}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                            {getStatusLabel(appointment.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAppointmentsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPatientsPage;